"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, Camera, CircleCheck as CheckCircle2, Disc, Droplets, Gauge, PhoneCall, Search, Settings, ShieldCheck, Sparkles, Wrench, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
const diagnosticServices = [{
  id: "camera-inspection",
  title: "High-Definition Sewer Camera Inspection",
  badge: "Non-Invasive Diagnostic",
  description: "Full-color, real-time video scoping through cleanouts to pinpoint pipe collapses, tree root intrusion, bellied lines, and severe grease blockages without excavation.",
  specifications: [{
    label: "Reach Capacity",
    value: "Up to 200 Feet"
  }, {
    label: "Pipe Diameter Range",
    value: "2 inch to 12 inch lines"
  }, {
    label: "Sonde Transmitter",
    value: "512 Hz precision locator"
  }, {
    label: "Reporting",
    value: "Instant digital footage & report"
  }],
  idealFor: ["Recurring line backups", "Pre-purchase home inspections", "Locating underground root penetration", "Identifying cracked or shifted clay pipes"]
}, {
  id: "smoke-testing",
  title: "Vapor & Smoke Pressure Testing",
  badge: "Odor & Leak Pinpointing",
  description: "Harmless, non-toxic pressurized visual vapor testing injected into the drain-waste-vent (DWV) system to identify elusive sewer gas leaks, cracked vent pipes, and dry traps.",
  specifications: [{
    label: "Vapor Type",
    value: "Non-toxic, residue-free aerosol"
  }, {
    label: "System Pressure",
    value: "0.5 to 1.5 PSI low-static"
  }, {
    label: "Detection Speed",
    value: "Instant visual exit indication"
  }, {
    label: "Application",
    value: "Residential DWV & commercial stacks"
  }],
  idealFor: ["Persistent sulfur or sewer smells indoors", "Unexplained bathroom odors", "Hidden wall cavity leaks", "Rodent entry point identification"]
}, {
  id: "static-pressure",
  title: "Hydrostatic Line & Pressure Testing",
  badge: "Structural Integrity",
  description: "Isolation and pressurized water testing of underground sanitary lines to confirm exact leak locations beneath concrete slab foundations in Jonesboro properties.",
  specifications: [{
    label: "Test Medium",
    value: "Clean water under monitored head"
  }, {
    label: "Isolation Method",
    value: "Inflatable test balls / plugs"
  }, {
    label: "Precision Rate",
    value: "99.4% leak loss accuracy"
  }, {
    label: "Foundation Safety",
    value: "100% non-destructive"
  }],
  idealFor: ["Suspected slab leaks", "Unexplained water meter movement", "Post-repair structural certification", "Foundation settling or soil erosion checks"]
}];
const cleaningMethods = [{
  id: "hydro-jetting",
  name: "Commercial-Grade Hydro Jetting",
  tagline: "4,000 PSI High-Pressure Water Scouring",
  icon: Droplets,
  description: "High-pressure forward and reverse water streams thoroughly scrub sludge, hardened grease, scale, and fibrous root systems from the inner pipe perimeter, restoring full inside diameter.",
  metrics: [{
    label: "Max Pressure",
    value: "4,000 PSI"
  }, {
    label: "Flow Rate",
    value: "18 GPM"
  }, {
    label: "Cleaning Radius",
    value: "360-Degree wall contact"
  }, {
    label: "Eco-Safety",
    value: "100% Chemical-Free"
  }],
  pros: ["Completely strips grease & mineral calcification", "Flushes out pulverized tree root debris", "Longest lasting clearing method available", "Safe for PVC, cast iron, and modern lined pipes"]
}, {
  id: "motorized-snaking",
  name: "Electromechanical Cable Augering",
  tagline: "Heavy-Duty Continuous Feed Snaking",
  icon: Disc,
  description: "Continuous-feed steel cables equipped with specialized cutter heads penetrate through stubborn localized clogs, feminine hygiene blockages, hair tangles, and dense obstructions.",
  metrics: [{
    label: "Cable Diameter",
    value: '3/4" heavy inner core'
  }, {
    label: "Head Attachments",
    value: "Spade, root cutter, bulb head"
  }, {
    label: "Reach Distance",
    value: "125 Feet"
  }, {
    label: "Response Rate",
    value: "Immediate flow clearance"
  }],
  pros: ["Rapid relief for immediate emergency toilet & sink backups", "Breaks tough mechanical blockages quickly", "Compact footprint for tight interior access", "Cost-effective rapid intervention"]
}, {
  id: "chain-knocking",
  name: "High-Speed Mechanical Chain Descaling",
  tagline: "Cast Iron Pipe Internal Restoration",
  icon: Settings,
  description: "High-speed rotating carbide-tipped chain knockers remove heavy tuberculation, rust blisters, and mineral scale from aging cast iron lines without requiring trenching.",
  metrics: [{
    label: "Shaft Rotation",
    value: "Up to 2,800 RPM"
  }, {
    label: "Chain Types",
    value: "Carbide-tipped / circular smooth"
  }, {
    label: "Pipe Sizes",
    value: '2" to 8" lines'
  }, {
    label: "Outcome",
    value: "Smooth internal line ready for lining"
  }],
  pros: ["Restores original pipe flow capacity", "Eliminates snag points that cause repetitive backups", "Extends the operational life of aging cast iron", "Prepares sewer pipes for CIPP trenchless relining"]
}];
const processSteps = [{
  step: "01",
  title: "Emergency Intake & Access",
  desc: "Rapid arrival with fully equipped mobile units. Technicians locate outdoor cleanouts or mechanical access points without messy indoor tear-outs."
}, {
  step: "02",
  title: "Real-Time Camera Scoping",
  desc: "A fiber-optic camera travels down the line. We show you the exact blockage, depth, and pipe condition live on our field diagnostic monitor."
}, {
  step: "03",
  title: "Targeted Mechanical Clearing",
  desc: "We select the appropriate tool—hydro-jetting, heavy cable auger, or descaler—to thoroughly clear and wash the line to street sewer mains."
}, {
  step: "04",
  title: "Post-Cleaning Verification",
  desc: "A final camera run verifies complete obstruction removal and confirms pristine interior pipe walls before job sign-off."
}];
export default function DrainDiagnostics() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedDiagnostic, setSelectedDiagnostic] = useState(diagnosticServices[0].id);
  const activeDiagnosticData = diagnosticServices.find(item => item.id === selectedDiagnostic) || diagnosticServices[0];
  return <section id="draindiagnosticsandcleaning" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            Precision Pipeline Engineering
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Technical Drain Diagnostics & Clearing Systems
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            We do not guess underground problems. Our Jonesboro plumbers deploy fiber-optic cameras, digital pipe locators, and 4,000 PSI hydro-jetters to resolve heavy clogs and recurring backups with exact precision.
          </p>
        </div>

        {/* Section Tabs: Diagnostics vs Cleaning Methods */}
        <Tabs defaultValue="diagnostics" className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="h-12 p-1.5 bg-muted rounded-xl border border-border">
              <TabsTrigger value="diagnostics" className="px-5 sm:px-8 py-2 text-sm sm:text-base font-semibold rounded-lg transition-all">
                <Search className="size-4 mr-2" />
                Pipeline Diagnostics
              </TabsTrigger>
              <TabsTrigger value="cleaning" className="px-5 sm:px-8 py-2 text-sm sm:text-base font-semibold rounded-lg transition-all">
                <Wrench className="size-4 mr-2" />
                Clearing Methods
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: DIAGNOSTIC SYSTEMS */}
          <TabsContent value="diagnostics" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Diagnostic Selection List */}
              <div className="lg:col-span-5 space-y-4">
                <div className="text-sm font-semibold tracking-wide uppercase text-muted-foreground px-1">
                  Select Diagnostic Protocol
                </div>
                {diagnosticServices.map((diag, index) => {
                const isSelected = diag.id === activeDiagnosticData.id;
                return <div data-index={index} key={diag.id}>
                      <Card onClick={() => setSelectedDiagnostic(diag.id)} className={`cursor-pointer transition-[border-color,box-shadow,background-color] rounded-xl border ${isSelected ? "border-primary bg-secondary/50 shadow-md ring-1 ring-primary/20" : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"}`}>
                        <CardHeader className="p-5 pb-3">
                          <div className="flex items-center justify-between gap-2">
                            <Badge variant={isSelected ? "default" : "outline"} className="text-xs">
                              {diag.badge}
                            </Badge>
                            {isSelected && <CheckCircle2 className="size-4 text-primary shrink-0" />}
                          </div>
                          <CardTitle className="text-lg font-bold mt-2 font-default text-card-foreground">
                            {diag.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-5 pb-5 pt-0">
                          <CardDescription className="text-sm text-muted-foreground line-clamp-2">
                            {diag.description}
                          </CardDescription>
                        </CardContent>
                      </Card>
                    </div>;
              })}
              </div>

              {/* Diagnostic Active Detail Card */}
              <div className="lg:col-span-7">
                <AnimatePresence mode="wait">
                  <motion.div key={activeDiagnosticData.id} initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: -12
                }} transition={{
                  duration: 0.25
                }}>
                    <Card className="border border-border bg-card shadow-lg rounded-2xl overflow-hidden p-0">
                      <div className="bg-primary px-6 py-6 sm:px-8 sm:py-8 text-primary-foreground">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-primary-foreground/15 px-3 py-1 rounded-md text-primary-foreground">
                            <Camera className="size-3.5" />
                            Diagnostic Specification
                          </span>
                          <span className="text-xs font-mono text-primary-foreground/80">
                            Jonesboro Unit Inspection
                          </span>
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-bold font-default text-primary-foreground mt-2">
                          {activeDiagnosticData.title}
                        </h3>
                        <p className="mt-3 text-sm sm:text-base text-primary-foreground/90 leading-relaxed">
                          {activeDiagnosticData.description}
                        </p>
                      </div>

                      <CardContent className="p-6 sm:p-8 space-y-6">
                        {/* Technical Specs Grid */}
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
                            Equipment Specifications
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {activeDiagnosticData.specifications.map((spec, sIdx) => <div data-index={sIdx} key={sIdx} className="bg-muted/60 border border-border/80 rounded-lg p-3.5">
                                <div className="text-xs text-muted-foreground font-medium">
                                  {spec.label}
                                </div>
                                <div className="text-sm font-semibold text-foreground mt-0.5">
                                  {spec.value}
                                </div>
                              </div>)}
                          </div>
                        </div>

                        <Separator />

                        {/* Problem Indicators / Ideal For */}
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                            When This Inspection Is Recommended
                          </h4>
                          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {activeDiagnosticData.idealFor.map((item, iIdx) => <li data-index={iIdx} key={iIdx} className="flex items-start gap-2.5 text-sm text-card-foreground">
                                <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                                <span>{item}</span>
                              </li>)}
                          </ul>
                        </div>

                        <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 bg-secondary/60 p-4 rounded-xl border border-border">
                          <div className="text-xs sm:text-sm text-secondary-foreground font-medium">
                            Need immediate video diagnostic inspection in Jonesboro?
                          </div>
                          <Button asChild size="sm" className="font-semibold shrink-0">
                            <Link to="/contact/">
                              <PhoneCall className="size-3.5 mr-1.5" />
                              Request Inspection
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </TabsContent>

          {/* TAB 2: CLEARING & CLEANING METHODS */}
          <TabsContent value="cleaning" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {cleaningMethods.map((method, index) => {
              const MethodIcon = method.icon;
              return <div data-index={index} key={method.id} className="h-full">
                    <Card className="h-full flex flex-col justify-between border border-border bg-card rounded-2xl shadow-md hover:shadow-lg transition-[border-color,box-shadow] p-6">
                      <div className="space-y-5">
                        <div className="flex items-center justify-between">
                          <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            <MethodIcon className="size-6" />
                          </div>
                          <Badge variant="secondary" className="font-mono text-xs">
                            {method.metrics[0].value}
                          </Badge>
                        </div>

                        <div>
                          <CardTitle className="text-xl font-bold font-default text-card-foreground">
                            {method.name}
                          </CardTitle>
                          <p className="text-xs font-semibold text-primary mt-1">
                            {method.tagline}
                          </p>
                          <CardDescription className="text-sm text-muted-foreground mt-3 leading-relaxed">
                            {method.description}
                          </CardDescription>
                        </div>

                        <Separator />

                        {/* Engineering Metrics */}
                        <div className="space-y-2">
                          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            Operational Parameters
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {method.metrics.map((metric, mIdx) => <div data-index={mIdx} key={mIdx} className="bg-muted/70 p-2.5 rounded-md border border-border/60">
                                <span className="text-muted-foreground block text-[11px]">
                                  {metric.label}
                                </span>
                                <span className="font-semibold text-foreground text-xs">
                                  {metric.value}
                                </span>
                              </div>)}
                          </div>
                        </div>

                        {/* Advantages */}
                        <div className="space-y-2">
                          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            Key Capabilities
                          </div>
                          <ul className="space-y-1.5">
                            {method.pros.map((pro, pIdx) => <li data-index={pIdx} key={pIdx} className="flex items-start gap-2 text-xs text-card-foreground">
                                <CheckCircle2 className="size-3.5 text-chart-4 shrink-0 mt-0.5" />
                                <span>{pro}</span>
                              </li>)}
                          </ul>
                        </div>
                      </div>

                      <div className="pt-6 mt-6 border-t border-border">
                        <Button asChild variant="outline" className="w-full justify-between font-semibold group hover:bg-primary hover:text-primary-foreground transition-colors">
                          <Link to="/services/">
                            <span>Book Method</span>
                            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </Card>
                  </div>;
            })}
            </div>
          </TabsContent>
        </Tabs>

        {/* 4-Step Technical Workflow Section */}
        <div className="mt-20 pt-16 border-t border-border">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Step-By-Step Standard
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground mt-2">
              Our Certified Clearing Protocol
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Every emergency drain service call follows an engineered workflow to protect your pipes and avoid repeated stoppages.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((stepItem, index) => <div data-index={index} key={stepItem.step} className="relative">
                <Card className="h-full border border-border bg-card shadow-xs rounded-xl p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-black font-mono text-primary/30">
                        {stepItem.step}
                      </span>
                      <div className="size-2 rounded-full bg-accent" />
                    </div>
                    <h4 className="text-base font-bold font-default text-card-foreground mb-2">
                      {stepItem.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {stepItem.desc}
                    </p>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Emergency Fast Action Banner */}
        <div className="mt-16 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-10 shadow-xl border border-primary/20 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center lg:text-left space-y-2">
            <div className="inline-flex items-center gap-2 bg-primary-foreground/15 text-primary-foreground text-xs font-semibold px-3 py-1 rounded-md">
              <Zap className="size-3.5" />
              24/7 Rapid Emergency Response in Jonesboro
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-primary-foreground">
              Experiencing a Severe Drain Backup Right Now?
            </h3>
            <p className="text-sm sm:text-base text-primary-foreground/80 leading-relaxed">
              Don't wait for sewer water to damage floors and walls. Our camera diagnostic trucks are stationed locally with hydro-jetting equipment ready for immediate dispatch.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" variant="secondary" className="font-bold text-secondary-foreground shadow-md">
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-2" />
                Dispatch Plumber
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="font-semibold bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/problems/">
                View Common Problems
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}